import axiosInstance from '../api';
import {
  GetAllUsersRes,
  GetUserByIdRes,
  UpdateUserRoleReq,
  UpdateUserRoleRes,
  RestoreUserRes,
  DeleteUserRes,
  ResetUserPasswordRes,
  CreateUserRes,
} from './userApi.types';

/**
 * 全ユーザーを取得する (管理者用)
 * @returns ユーザーリスト
 */
export const getAllUsers = async (): Promise<GetAllUsersRes> => {
  try {
    const response = await axiosInstance.get<GetAllUsersRes>('/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

/**
 * 全ユーザーを取得する（削除済みユーザーを含む）(管理者用)
 * @param includeDeleted 削除済みユーザーを含めるか
 * @returns ユーザーリスト
 */
export const getAllUsersWithDeleted = async (includeDeleted = true): Promise<GetAllUsersRes> => {
  try {
    const response = await axiosInstance.get<GetAllUsersRes>('/users', {
      params: { include_deleted: includeDeleted }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all users with deleted:', error);
    throw error;
  }
};

/**
 * ユーザーIDを指定して1人のユーザー情報を取得する
 * @param userId ユーザーID
 * @returns ユーザー情報
 */
export const getUserById = async (userId: string | number): Promise<GetUserByIdRes> => {
  try {
    const response = await axiosInstance.get<GetUserByIdRes>(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${userId}:`, error);
    throw error;
  }
};

/**
 * ユーザーの権限を変更する (管理者用)
 * @param userId ユーザーID
 * @param role 新しい権限 (user/admin)
 * @returns 更新されたユーザー情報
 */
export const updateUserRole = async (
  userId: string | number, 
  role: 'user' | 'admin'
): Promise<UpdateUserRoleRes> => {
  try {
    const payload: UpdateUserRoleReq = { role };
    const response = await axiosInstance.put<UpdateUserRoleRes>(`/users/${userId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${userId} role:`, error);
    throw error;
  }
};

/**
 * 削除済みユーザーを復元する (管理者用)
 * @param userId ユーザーID
 * @returns 復元されたユーザー情報
 */
export const restoreUser = async (userId: string | number): Promise<RestoreUserRes> => {
  try {
    const response = await axiosInstance.put<RestoreUserRes>(`/users/${userId}/restore`);
    return response.data;
  } catch (error) {
    console.error(`Error restoring user ${userId}:`, error);
    throw error;
  }
};

/**
 * ユーザーを論理削除する (管理者用)
 * @param userId ユーザーID
 * @returns 削除されたユーザー情報
 */
export const deleteUser = async (userId: string | number): Promise<DeleteUserRes> => {
  try {
    const response = await axiosInstance.delete<DeleteUserRes>(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${userId}:`, error);
    throw error;
  }
};

/**
 * 管理者がユーザーのパスワードをリセットする
 * @param userId リセット対象のユーザーID
 * @returns 一時パスワードを含むレスポンス
 */
export const resetUserPassword = async (userId: string | number): Promise<ResetUserPasswordRes> => {
  try {
    const response = await axiosInstance.post<ResetUserPasswordRes>(`/users/${userId}/reset-password`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createUser = async (email: string, name?: string): Promise<CreateUserRes> => {
  try {
    const response = await axiosInstance.post<CreateUserRes>('/users', { email, name });
    return response.data;
  } catch (error) {
    throw error;
  }
}; 